import { Trans } from 'react-i18next'
import { Base } from '@itsrever/design-system'
import { formatPrice } from '@/utils'
import { ListDate } from '@/ui/shared/list-tabs'
import { PbPublicReturnProcess } from '@itsrever/rever-api'

const { Badge, createColumnHelper } = Base

const columnHelper = createColumnHelper<PbPublicReturnProcess>()

export const columns = [
  columnHelper.accessor('customer_printed_order_id', {
    header: () => <Trans i18nKey="orders.order_id" />
  }),
  columnHelper.accessor('started_at', {
    header: () => <Trans i18nKey="orders.return_date" />,
    cell: (info) => {
      const date = info.getValue()

      if (date) {
        return <ListDate date={new Date(date)} />
      }
    },
    sortDescFirst: true,
    meta: {
      cellClassName: 'whitespace-nowrap',
      headClassName: 'whitespace-nowrap'
    }
  }),
  columnHelper.accessor('customer.first_name', {
    header: () => <Trans i18nKey="orders.customer" />,
    cell: (info) => (
      <span className="block max-w-36 truncate font-sans">
        {info.getValue()} {info.row.original.customer?.last_name}
      </span>
    )
  }),
  columnHelper.accessor('ecommerce_id', {
    header: () => <Trans i18nKey="orders.shop" />,
    cell: (info) => (
      <span className="block max-w-36 truncate font-sans">
        {info.getValue()}
      </span>
    )
  }),
  columnHelper.accessor('line_items', {
    header: () => <Trans i18nKey="orders.items" />,
    cell: (info) => {
      const products = info
        .getValue()
        ?.filter((item) => item.type === 'product')

      const totalItems = products?.reduce(
        (acc, prod) => acc + (prod.quantity ?? 0),
        0
      )

      return totalItems
    }
  }),
  columnHelper.accessor('executed_total', {
    header: () => <Trans i18nKey="orders.compensation" />,
    cell: (info) => {
      const hasExchange =
        info.row.original.exchange_refund_amount &&
        Number(info.row.original.exchange_refund_amount) > 0
      const hasRefund =
        (info.row.original.bank_transfer_refund_amount &&
          Number(info.row.original.bank_transfer_refund_amount) > 0) ||
        (info.row.original.original_pm_refund_amount &&
          Number(info.row.original.original_pm_refund_amount) > 0)
      const hasPromocode =
        info.row.original.promocode_refund_amount &&
        Number(info.row.original.promocode_refund_amount) > 0
      const hasGiftcard =
        info.row.original.gift_refund_amount &&
        Number(info.row.original.gift_refund_amount) > 0
      const chips: string[] = []
      if (hasExchange) chips.push('refund_methods.EXCHANGE')
      if (hasRefund) chips.push('refund_methods.REFUND')
      if (hasPromocode) chips.push('refund_methods.PROMO_CODE')
      if (hasGiftcard) chips.push('refund_methods.GIFT_CARD')

      if (chips.length === 0) {
        return <span className="text-neutral-400">—</span>
      }

      return (
        <div className="flex gap-1">
          {chips.map((key) => (
            <Badge key={key}>
              <Trans i18nKey={key} />
            </Badge>
          ))}
        </div>
      )
    },
    meta: {
      cellClassName: 'whitespace-nowrap',
      headClassName: 'whitespace-nowrap'
    }
  }),
  columnHelper.accessor('currency_money_format', {
    header: () => <Trans i18nKey="orders.total" className="text-right" />,
    cell: (info) => {
      const totalRefund =
        Number(info.row.original.gift_refund_amount ?? 0) +
        Number(info.row.original.bank_transfer_refund_amount ?? 0) +
        Number(info.row.original.original_pm_refund_amount ?? 0) +
        Number(info.row.original.promocode_refund_amount ?? 0)

      const displayRefund = totalRefund >= 0 ? totalRefund : 0

      return formatPrice(displayRefund, info.getValue() ?? {})
    },
    meta: {
      cellClassName: 'text-right whitespace-nowrap',
      headClassName: 'text-right whitespace-nowrap'
    }
  }),
  columnHelper.accessor('return_method', {
    header: () => <Trans i18nKey="orders.return_method" />,
    cell: (info) => {
      const value = info.getValue()
      if (!value || value === 'NO_RETURN_METHOD') {
        return <span className="text-neutral-400">—</span>
      }
      return <Trans i18nKey={`return_methods.${value}`} />
    }
  }),
  columnHelper.accessor(
    (row) => row.pickup_address?.country ?? row.drop_off_address?.country,
    {
      id: 'country',
      header: () => <Trans i18nKey="orders.country" />,
      cell: (info) => {
        const value = info.getValue()
        if (!value) return <span className="text-neutral-400">—</span>
        return value
      },
      meta: {
        cellClassName: 'whitespace-nowrap',
        headClassName: 'whitespace-nowrap'
      }
    }
  ),
  columnHelper.accessor('user_selected_carrier', {
    header: () => <Trans i18nKey="orders.carrier" />,
    cell: (info) => {
      const carrier = info.getValue()

      if (!carrier || carrier === 'NONE') {
        return <span className="text-neutral-400">—</span>
      }

      return carrier
    },
    meta: {
      cellClassName: 'uppercase'
    }
  }),
  columnHelper.accessor('warehouse', {
    header: () => <Trans i18nKey="orders.warehouse" />,
    cell: (info) => {
      const warehouse = info.getValue()
      const label = [warehouse?.city, warehouse?.name]
        .filter(Boolean)
        .join(' - ')
      return label ? (
        <span className="block max-w-36 truncate font-sans">{label}</span>
      ) : (
        <span className="text-neutral-400">—</span>
      )
    }
  }),
  columnHelper.accessor('logistics_status', {
    header: () => <Trans i18nKey="orders.logistics_status" />,
    cell: (info) => {
      switch (info.getValue()) {
        case 'LOGISTICS_STATUS_LABEL_PENDING':
          return (
            <Badge tone="warning">
              <Trans i18nKey="logistics_status.label_pending" />
            </Badge>
          )
        case 'LOGISTICS_STATUS_CREATED':
          return (
            <Badge tone="info">
              <Trans i18nKey="logistics_status.label_created" />
            </Badge>
          )
        case 'LOGISTICS_STATUS_COLLECTED':
          return (
            <Badge tone="info">
              <Trans i18nKey="logistics_status.collected" />
            </Badge>
          )
        case 'LOGISTICS_STATUS_WAREHOUSE':
          return (
            <Badge tone="success">
              <Trans i18nKey="logistics_status.warehouse" />
            </Badge>
          )
        case 'LOGISTICS_STATUS_NA':
          return <span className="text-neutral-400">—</span>
        default:
          return null
      }
    }
  }),
  columnHelper.accessor('refund_status', {
    header: () => <Trans i18nKey="orders.refund_status" />,
    cell: (info) => {
      switch (info.getValue()) {
        case 'REFUND_STATUS_PENDING':
          return (
            <Badge tone="warning">
              <Trans i18nKey="refund_status.pending" />
            </Badge>
          )
        case 'REFUND_STATUS_PROCESSING':
          return (
            <Badge tone="info">
              <Trans i18nKey="refund_status.processing" />
            </Badge>
          )
        case 'REFUND_STATUS_COMPLETED':
          return (
            <Badge tone="success">
              <Trans i18nKey="refund_status.completed" />
            </Badge>
          )
        case 'REFUND_STATUS_NA':
          return <span className="text-neutral-400">—</span>
        default:
          return null
      }
    }
  })
]
